import {
  Box,
  CircularProgress,
  InputAdornment,
  type InputBaseComponentProps,
  OutlinedInput,
} from '@mui/material';
import { Command } from 'cmdk';
import { ChevronDown, ChevronsDown, Plus, Search } from 'lucide-react';
import {
  type CSSProperties,
  forwardRef,
  type KeyboardEvent,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { groupBy, isTruthy } from 'remeda';
import {
  CommandRoot,
  MenuStatus,
  OptionCheckbox,
  OptionRow,
} from 'src/components/SelectMenu/select-menu.elements';
import { useDebounce } from 'src/utils/hooks/use-debounce';
import type { ModalListFilter, ModalListItem, ModalListProps } from './modal-container.types';
import {
  CREATE_ROW_ATTRIBUTE,
  filterAdornmentSx,
  FOLDER_OPEN_ATTRIBUTE,
  FOLDER_ROW_ATTRIBUTE,
  getFolderChevronSx,
  getModalListRootSx,
  LOAD_MORE_ROW_ATTRIBUTE,
  NESTED_END_ATTRIBUTE,
  NESTED_ROW_ATTRIBUTE,
  pinnedCreateDividerSx,
  pinnedRowsScrollSx,
  ROW_LEVEL_VARIABLE,
  rowActionsSx,
  titleEditorSx,
} from './modal-list.styles';
import { useModalContainerContext } from './modal-steps-context';

const UNGROUPED = '';

const CREATE_ROW_VALUE = 'modal-list-create-row';

const LOAD_MORE_ROW_VALUE = 'modal-list-load-more-row';

const CENTERED_ROW_STYLE: CSSProperties = { alignItems: 'center' };

const EXPANDED_BY_KEY: Partial<Record<string, boolean>> = {
  ArrowRight: true,
  ArrowLeft: false,
};

const getRowStyle = ({
  description,
  level = 0,
  pinnedFirst,
}: ModalListItem): CSSProperties => ({
  ...(!description && CENTERED_ROW_STYLE),
  ...(level > 0 && { [ROW_LEVEL_VARIABLE]: level }),
  ...(pinnedFirst && { order: -1 }),
});

// A folder row and the rows inside it are painted as one block, so each row needs to know
// whether a nested row follows it (an open folder) or ends the block (its last nested row).
const getFolderAttributes = (item: ModalListItem, next: ModalListItem | undefined) => {
  const level = item.level ?? 0;
  const nextLevel = next?.level ?? 0;
  return {
    [FOLDER_ROW_ATTRIBUTE]: item.expanded === undefined ? undefined : true,
    [FOLDER_OPEN_ATTRIBUTE]: item.expanded && nextLevel > level ? true : undefined,
    [NESTED_ROW_ATTRIBUTE]: level > 0 ? true : undefined,
    [NESTED_END_ATTRIBUTE]: level > 0 && nextLevel < level ? true : undefined,
  };
};

const keepInRowControl = (event: SyntheticEvent) => event.stopPropagation();

// Row buttons keep Enter and the arrows from the list, but Escape still closes the modal.
const keepKeysInRowActions = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') {
    event.stopPropagation();
  }
};

// Rows are keyed by id, so the filter reads only the keywords; matching the value would match ids.
const matchKeywords: ModalListFilter = (_value, search, keywords = []) => {
  const term = search.toLowerCase();
  return keywords.some((keyword) => keyword.toLowerCase().includes(term)) ? 1 : 0;
};

const getItemKeywords = ({ title, description, keywords = [] }: ModalListItem) =>
  [title, description, ...keywords].filter(isTruthy);

// MUI hands its inputComponent the native input's props and ref; cmdk's input keeps the
// combobox role and aria-activedescendant on the element that actually holds focus.
const CommandFilterInput = forwardRef<HTMLInputElement, InputBaseComponentProps>(
  (props, ref) => <Command.Input ref={ref} {...props} />,
);

CommandFilterInput.displayName = 'CommandFilterInput';

/** The list part of a ModalContainer picker: a filter that keeps focus, and one highlight. */
export const ModalList = <ItemT extends ModalListItem>({
  items,
  onSelect,
  onExpandedChange,
  placeholder = 'Type to filter…',
  emptyText = 'No results found.',
  loading = false,
  shouldFilter = true,
  filter = matchKeywords,
  onSearchChange,
  searchDebounce = 0,
  createAction,
  loadMore,
  readOnly = false,
  belowFilter,
  highlightedId,
  onHighlightChange,
  testId,
  ref,
}: ModalListProps<ItemT>) => {
  const { fullScreen } = useModalContainerContext();
  const [search, setSearch] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  // cmdk scrolls only for keyboard moves; a highlight set by the caller has to be brought into view.
  useEffect(() => {
    if (highlightedId === undefined) {
      return;
    }
    listRef.current
      ?.querySelector(`[cmdk-item][data-value="${CSS.escape(highlightedId)}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedId]);
  const [ownActiveId, setOwnActiveId] = useState('');
  const activeId = highlightedId ?? ownActiveId;
  const filterRef = useRef<HTMLInputElement>(null);
  const isEditingRow = items.some((item) => Boolean(item.titleEditor));
  const debouncedSearchChange = useDebounce(
    (value: string) => onSearchChange?.(value),
    searchDebounce,
  );

  // Without a debounce the caller hears the search in the same render, so a row it derives
  // from the text (a parsed date, a custom prompt) is there when cmdk picks the first highlight.
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (searchDebounce > 0) {
        debouncedSearchChange(value.trim());
      } else {
        onSearchChange?.(value.trim());
      }
    },
    [debouncedSearchChange, onSearchChange, searchDebounce],
  );

  // Filtered here, not by cmdk: cmdk re-sorts matches by moving DOM nodes, which would pull
  // the Create row out of last place.
  const sections = useMemo(() => {
    const term = search.trim();
    const visible =
      shouldFilter && term
        ? items.filter((item) => filter(item.id, term, getItemKeywords(item)) > 0)
        : items;
    return Object.entries(groupBy(visible, (item) => item.group ?? UNGROUPED));
  }, [items, search, shouldFilter, filter]);

  const showCreateRow =
    createAction !== undefined &&
    (createAction.showWhen !== 'noMatches' || (search.trim() !== '' && sections.length === 0));

  // A row editor takes focus from the filter and unmounts when it is done, which leaves focus on
  // the document body; hand it back so the arrows and Enter work again.
  useEffect(() => {
    if (!isEditingRow && document.activeElement === document.body) {
      filterRef.current?.focus();
    }
  }, [isEditingRow]);

  const handleActiveChange = (id: string) => {
    setOwnActiveId(id);
    onHighlightChange?.(id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const expand = EXPANDED_BY_KEY[event.key];
    if (expand === undefined || search !== '' || !onExpandedChange) {
      return;
    }
    const item = items.find(({ id }) => id === activeId);
    if (item?.expanded === undefined || item.expanded === expand) {
      return;
    }
    event.preventDefault();
    onExpandedChange(item, expand);
  };

  const handleItemSelect = (item: ItemT) => {
    if (item.titleEditor) {
      return;
    }
    if (item.expanded === undefined) {
      onSelect?.(item);
    } else {
      onExpandedChange?.(item, !item.expanded);
    }
  };

  const renderItem = (item: ItemT, index: number, rows: ItemT[]) => (
    <Command.Item
      key={item.id}
      value={item.id}
      disabled={readOnly || item.disabled}
      onSelect={() => handleItemSelect(item)}
      data-expanded={item.expanded}
      // cmdk owns aria-selected (the highlight), so the checked state goes on aria-checked.
      aria-checked={item.checked}
      data-testid={testId ? `${testId}-option-${item.id}` : undefined}
      style={getRowStyle(item)}
      {...getFolderAttributes(item, rows[index + 1])}
    >
      <OptionRow
        icon={item.icon}
        avatar={item.avatar}
        title={
          item.titleEditor ? (
            <Box
              component="span"
              sx={titleEditorSx}
              onKeyDown={keepInRowControl}
              onClick={keepInRowControl}
            >
              {item.titleEditor}
            </Box>
          ) : (
            item.title
          )
        }
        description={item.description}
        endAdornment={
          <>
            {item.endAdornment}
            {item.actions ? (
              <Box
                component="span"
                sx={rowActionsSx}
                onKeyDown={keepKeysInRowActions}
                onClick={keepInRowControl}
              >
                {item.actions}
              </Box>
            ) : null}
            {item.checked === undefined ? null : <OptionCheckbox checked={item.checked} />}
            {item.expanded === undefined ? null : (
              <Box component="span" sx={getFolderChevronSx(item.expanded)}>
                <ChevronDown aria-hidden />
              </Box>
            )}
          </>
        }
      />
    </Command.Item>
  );

  const listRows = (
    <>
      {loading && items.length === 0 ? (
        <Command.Loading>
          <MenuStatus>Loading…</MenuStatus>
        </Command.Loading>
      ) : (
        <Command.Empty>
          <MenuStatus>{emptyText}</MenuStatus>
        </Command.Empty>
      )}
      {sections.map(([group, rows]) =>
        group === UNGROUPED ? (
          rows.map(renderItem)
        ) : (
          <Command.Group key={group} heading={group}>
            {rows.map(renderItem)}
          </Command.Group>
        ),
      )}
      {loadMore ? (
        // forceMount keeps it out of cmdk's match count, so the empty line still shows.
        <Command.Item
          forceMount
          value={LOAD_MORE_ROW_VALUE}
          onSelect={() => {
            if (!loadMore.loading) {
              loadMore.onSelect();
            }
          }}
          data-testid={testId ? `${testId}-load-more` : undefined}
          style={CENTERED_ROW_STYLE}
          {...{ [LOAD_MORE_ROW_ATTRIBUTE]: true }}
        >
          <OptionRow
            icon={
              loadMore.loading ? (
                <CircularProgress size="0.875rem" color="inherit" aria-label="Loading" />
              ) : (
                <ChevronsDown />
              )
            }
            iconSx={{ backgroundColor: 'transparent' }}
            title={loadMore.label ?? 'Load more'}
          />
        </Command.Item>
      ) : null}
    </>
  );

  const createRow = showCreateRow ? (
    // forceMount keeps it out of cmdk's match count, so the empty line still shows.
    <Command.Item
      forceMount
      value={CREATE_ROW_VALUE}
      onSelect={() => createAction.onSelect()}
      // Keeps a click from pulling focus off the field a create step focuses on open.
      onMouseDown={(event) => event.preventDefault()}
      data-testid={testId ? `${testId}-create` : undefined}
      style={CENTERED_ROW_STYLE}
      {...{ [CREATE_ROW_ATTRIBUTE]: true }}
    >
      <OptionRow
        icon={<Plus />}
        iconSx={{ backgroundColor: 'transparent' }}
        title={createAction.label}
      />
    </Command.Item>
  ) : null;

  const isCreateRowPinned = Boolean(createAction?.pinned);

  return (
    <CommandRoot
      ref={ref}
      sx={getModalListRootSx({
        fullScreen,
        readOnly,
        pinnedCreateRow: isCreateRowPinned,
        pinnedFirstRow: items.some(({ pinnedFirst }) => pinnedFirst),
      })}
    >
      <Command
        loop
        shouldFilter={false}
        label={placeholder}
        value={activeId}
        onValueChange={handleActiveChange}
        onKeyDown={handleKeyDown}
      >
        <OutlinedInput
          fullWidth
          value={search}
          placeholder={placeholder}
          inputRef={filterRef}
          inputComponent={CommandFilterInput}
          inputProps={{
            onValueChange: handleSearchChange,
            'data-testid': testId ? `${testId}-search` : undefined,
          }}
          startAdornment={
            <InputAdornment position="start" sx={filterAdornmentSx}>
              {loading ? (
                <CircularProgress size="0.875rem" color="inherit" aria-label="Loading" />
              ) : (
                <Search aria-hidden />
              )}
            </InputAdornment>
          }
        />
        {/* The list has a fixed height, so anything above the rows must not shrink. */}
        {belowFilter ? <Box flexShrink={0}>{belowFilter}</Box> : null}
        <Command.List ref={listRef}>
          {isCreateRowPinned ? (
            <>
              <Box sx={pinnedRowsScrollSx}>{listRows}</Box>
              {createRow ? (
                <>
                  <Box sx={pinnedCreateDividerSx} />
                  {createRow}
                </>
              ) : null}
            </>
          ) : (
            <>
              {listRows}
              {createRow}
            </>
          )}
        </Command.List>
      </Command>
    </CommandRoot>
  );
};
